import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from "chart.js";
import React, { useContext, useEffect, useState } from "react";

import ArrowBox from "../common/ArrowBox";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { SVTUserContext } from "../../utils/SVTUserContext";
import { useTranslation } from "react-i18next";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

const SelfVerificationResults = ({ levelsData }) => {
    const [data, setData] = useState(null);
    const {
        setActiveTab,
        activeLevelId,
        setActiveLevelId,
        assessmentResult,
        templateName,
        setIsReview,
        assessmentResponses,
        scores,
        setScores,
    } = useContext(SVTUserContext);

    useEffect(() => {
        if (assessmentResult) {
            setData(assessmentResult);
        } else {
            setData({
                SubmittedDate:
                    assessmentResponses.find(
                        (item) => item.levelId === activeLevelId
                    )?.SubmittedDate || null,
                result:
                    assessmentResponses.find(
                        (item) => item.levelId === activeLevelId
                    )?.result || null,
            });
            setScores((prevState) => ({
                ...prevState,
                aggregateScore:
                    assessmentResponses.find(
                        (item) => item.levelId === activeLevelId
                    )?.aggregateScore || 0,
            }));
        }
    }, [assessmentResult, assessmentResponses, activeLevelId]);
    const { t } = useTranslation();
    const { SubmittedDate = null, result: templateResult = null } = data || {};

    const { basicScore, developingScore, matureScore, aggregateScore } = scores;

    const graphData = {
        labels: [
            t("basicPercentage"),
            t("developingPercentage"),
            t("maturePercentage"),
            t("aggregatePercentage"),
        ],
        datasets: [
            {
                label: "",
                data: [
                    Math.round(basicScore),
                    Math.round(developingScore),
                    Math.round(matureScore),
                    Math.round(aggregateScore || 0),
                ],
                backgroundColor: ["#E1ECFB", "#C7DFFF", "#4CA1E6", "#024B86"],
                barThickness: 40,
                datalabels: {
                    color: function (context) {
                        return context.dataIndex === 3 ? "#fff" : "#000";
                    },
                },
            },
        ],
    };

    const options = {
        scales: {
            y: {
                beginAtZero: true,
                max: 100,
            },
            x: {
                grid: { display: false },
            },
        },
        plugins: {
            datalabels: {
                color: "black",
                display: true,

                font: {
                    size: 12,
                    weight: "600",
                },
            },
            legend: {
                display: false,
            },
        },
    };

    const handleReviewAnswers = () => {
        setIsReview(true);
        setActiveTab("Tab3");
    };

    const onNextClick = () => {
        setActiveTab("Tab3");
        setActiveLevelId(activeLevelId + 1);
    };

    const { i18n } = useTranslation();
    const getDate = () => {
        const date = new Date(SubmittedDate);
        let locale = "en-US";
        if (i18n.language === "jp" || i18n.language === "japanese") {
            locale = "ja-JP";
        } else if (i18n.language === "ch" || i18n.language === "chinese") {
            locale = "zh-CN";
        }
        return date.toLocaleDateString(locale, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const getLevelName = () => {
        if (!levelsData || !activeLevelId) return "";
        const level = levelsData?.find(
            (level) => level.levelId === activeLevelId
        );
        return level ? t(level?.levelName?.toLowerCase()) : "";
    };

    const getTextPerScore = () => {
        if (activeLevelId === 1) {
            return basicScore === 100
                ? t("congratulationsBasic100")
                : t("improveBasicScore");
        }
        if (activeLevelId === 2) {
            if (developingScore === 100) {
                return matureScore === 0
                    ? t("congratulationsDeveloping100")
                    : t("perfectDevelopingScore100");
            }
            if (developingScore >= 50) {
                return matureScore === 0
                    ? t("developingScoreBetween50And100")
                    : t("developingScoreBetween50And100WithMature");
            }
            return t("developingScoreBelow50");
        }
        if (activeLevelId === 3) {
            return matureScore === 100
                ? t("perfectMatureScore100")
                : t("matureScoreBelow100");
        }
        return "";
    };

    return (
        <div className="assessment-container">
            <h2 className="assessment-header">{templateName}</h2>
            <div className="arrow-box-container">
                {levelsData &&
                    levelsData.map((level) => (
                        <ArrowBox
                            key={level.levelId}
                            level={level}
                            activeLevelId={activeLevelId}
                            isLast={
                                level.levelId ===
                                levelsData[levelsData.length - 1].levelId
                            }
                        />
                    ))}
            </div>
            <h4>
                {/* {t("thankyouforsubmittingthe")} {getLevelName()}{" "}
                {t("levelAssessment")} 
                &nbsp;{t("on")} {getDate()} / {templateName}. */}
                {t("submissionSummary", {
                    levelName: getLevelName(),
                    date: getDate(),
                    templateName: templateName
                })}
            </h4>
            <p className="svt-results">{getTextPerScore()}</p>
            {/* <div className="chart-and-button-container"> */}
            <div className="assessment-chart">
                <Bar data={graphData} options={options} />
            </div>
            <div className="graph-buttons-container">
                <button
                    className="greyButton"
                    onClick={() => {
                        setActiveTab("Tab2");
                        setScores({
                            basicScore: 0,
                            developingScore: 0,
                            matureScore: 0,
                            aggregateScore: 0,
                        });
                    }}
                >
                    {t("close")}
                </button>
                {assessmentResult && (
                    <button
                        className="secondaryButton"
                        onClick={handleReviewAnswers}
                    >
                        {t("reviewAnswers")}
                    </button>
                )}
                {activeLevelId === 1 && basicScore < 100 && (
                    <button
                        className="secondaryButton"
                        onClick={() => setActiveTab("Tab3")}
                    >
                        {t("retakeBasicAssessment")}
                    </button>
                )}
                {(activeLevelId === 2 || activeLevelId === 3) &&
                    developingScore < 100 && (
                        <button
                            className="secondaryButton"
                            onClick={() => {
                                setActiveTab("Tab3");
                                setActiveLevelId(2);
                            }}
                        >
                            {t("retakeDevelopingAssessment")}
                        </button>
                    )}
                {activeLevelId === 3 && matureScore < 100 && (
                    <button
                        className="secondaryButton"
                        onClick={() => {
                            setActiveTab("Tab3");
                        }}
                    >
                        {t("retakeMatureAssessment")}
                    </button>
                )}
                {activeLevelId < 3 &&
                    templateResult === 1 &&
                    matureScore < 100 && (
                        <button
                            className="secondaryButton"
                            onClick={onNextClick}
                        >
                            {activeLevelId === 2 && matureScore > 0
                                ? t("retakeMatureAssessment")
                                : t('next')}
                        </button>
                    )}
            </div>
        </div>
        // </div>
    );
};

export default SelfVerificationResults;
